import { useEffect, useRef, useState } from "react";
import { CheckCircle } from "lucide-react";
import { submitBugReport } from "@/lib/api";
import { useTeamsToken } from "@/lib/teamsTokenContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/Button";
import { Textarea } from "@/components/ui/Textarea";

interface BugReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** First 300 characters of the assistant message being reported. */
  messageContext?: string;
  /** The user's question that prompted this response, truncated to 120 chars. */
  question?: string;
}

type ModalState = "idle" | "submitting" | "success" | "error";

export function BugReportModal({ isOpen, onClose, messageContext, question }: BugReportModalProps) {
  const teamsToken = useTeamsToken();
  const [description, setDescription] = useState("");
  const [context, setContext] = useState("");
  const [state, setState] = useState<ModalState>("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isOpen) {
      setDescription("");
      setContext("");
      setState("idle");
      setErrorMessage("");
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  }, [isOpen]);

  useEffect(() => {
    if (state !== "success") return;
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    return () => clearTimeout(timer);
  }, [state, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim() || state === "submitting") return;

    setState("submitting");
    setErrorMessage("");

    try {
      await submitBugReport(
        {
          description: description.trim(),
          context: messageContext?.slice(0, 300),
          question: question?.slice(0, 120),
          timestamp: new Date().toISOString(),
        },
        teamsToken,
      );
      setState("success");
    } catch (err) {
      setState("error");
      setErrorMessage(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Report a problem</DialogTitle>
          <DialogDescription>
            We'll log this and look into it.
          </DialogDescription>
        </DialogHeader>

        {state === "success" ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <CheckCircle className="h-10 w-10 text-[var(--success)]" />
            <p className="text-[14px] font-medium text-text">
              Thanks — we've logged this and will look into it.
            </p>
            <p className="text-[12.5px] text-text-muted">This window will close automatically.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <div className="mb-4">
              <label
                htmlFor="bug-description"
                className="mb-1.5 block text-[13px] font-medium text-text"
              >
                What went wrong?
                <span aria-hidden className="ml-1 text-[var(--danger)]">*</span>
              </label>
              <Textarea
                id="bug-description"
                ref={textareaRef}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the issue in a few words…"
                maxLength={2000}
                rows={3}
                required
                disabled={state === "submitting"}
                className="rounded-lg border border-border bg-surface-muted px-3 py-2 text-[13.5px] placeholder:text-text-muted/60 focus:border-accent focus:ring-1 focus:ring-accent disabled:opacity-60"
              />
            </div>

            <div className="mb-5">
              <label
                htmlFor="bug-context"
                className="mb-1.5 block text-[13px] font-medium text-text"
              >
                What were you trying to find out?
                <span className="ml-1.5 text-[12px] font-normal text-text-muted">(optional)</span>
              </label>
              <Textarea
                id="bug-context"
                value={context}
                onChange={(e) => setContext(e.target.value)}
                placeholder='e.g. "Does the family portal support online payments?"'
                maxLength={500}
                rows={2}
                disabled={state === "submitting"}
                className="rounded-lg border border-border bg-surface-muted px-3 py-2 text-[13.5px] placeholder:text-text-muted/60 focus:border-accent focus:ring-1 focus:ring-accent disabled:opacity-60"
              />
            </div>

            {state === "error" && (
              <p
                role="alert"
                className="mb-4 rounded-lg border border-[var(--danger)]/30 bg-[var(--danger)]/5 px-3 py-2 text-[13px] text-[var(--danger)]"
              >
                {errorMessage}
              </p>
            )}

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onClose}
                disabled={state === "submitting"}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={!description.trim() || state === "submitting"}
              >
                {state === "submitting" ? "Submitting…" : "Submit"}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
